import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Brush,
} from 'recharts';
import './TradingMetricsChart.css';

export interface TradingMetricPoint {
  time: string;
  value: number;
  delta: number;
  percentage: number;
}

export interface OverlaidTradingMetrics {
  broker: string;
  ordersCreated: TradingMetricPoint[];
  ordersFilled: TradingMetricPoint[];
  ordersCanceled: TradingMetricPoint[];
  ordersFailed: TradingMetricPoint[];
  ordersTotal: TradingMetricPoint[];
}

export interface TradingMetricsSummary {
  totalCreated: number;
  totalFilled: number;
  totalCanceled: number;
  totalFailed: number;
  totalCount: number;
  filledPercentage: number;
  canceledPercentage: number;
  failedPercentage: number;
  successRate: number;
}

interface MetricConfig {
  key: string;
  label: string;
  color: string;
  strokeWidth: number;
  strokeDashArray?: string;
}

const METRIC_CONFIGS: MetricConfig[] = [
  { 
    key: 'ordersCreated', 
    label: 'Created', 
    color: '#3b82f6', 
    strokeWidth: 3 
  },
  { 
    key: 'ordersFilled', 
    label: 'Filled', 
    color: '#10b981', 
    strokeWidth: 3 
  },
  { 
    key: 'ordersCanceled', 
    label: 'Canceled', 
    color: '#f59e0b', 
    strokeWidth: 3 
  },
  { 
    key: 'ordersFailed', 
    label: 'Failed', 
    color: '#ef4444', 
    strokeWidth: 3,
    strokeDashArray: '5 5'
  },
];

interface OverlaidTradingMetricsChartProps {
  data: OverlaidTradingMetrics;
  summary: TradingMetricsSummary;
  showDelta?: boolean;
  isLoading?: boolean;
  onTimeRangeChange?: (startIndex: number, endIndex: number) => void;
}

export const TradingMetricsChart: React.FC<OverlaidTradingMetricsChartProps> = ({
  data,
  summary,
  showDelta = false,
  isLoading = false,
  onTimeRangeChange,
}) => {
  const [selectedMetrics, setSelectedMetrics] = useState<Set<string>>(
    new Set(METRIC_CONFIGS.map(config => config.key))
  );
  const [hoveredMetric, setHoveredMetric] = useState<string | null>(null);
  const [selectedLine, setSelectedLine] = useState<string | null>(null);
  const [brushRange, setBrushRange] = useState<{ startIndex?: number; endIndex?: number }>({});
  
  const chartRef = useRef<any>(null);

  // Combine all metrics into chartable data
  const chartData = useMemo(() => {
    if (!data.ordersCreated?.length) return [];
    
    return data.ordersCreated.map((point, index) => ({
      time: point.time,
      timestamp: new Date(point.time).getTime(),
      ordersCreated: showDelta ? point.delta : point.value,
      ordersFilled: showDelta ? data.ordersFilled[index]?.delta || 0 : data.ordersFilled[index]?.value || 0,
      ordersCanceled: showDelta ? data.ordersCanceled[index]?.delta || 0 : data.ordersCanceled[index]?.value || 0,
      ordersFailed: showDelta ? data.ordersFailed[index]?.delta || 0 : data.ordersFailed[index]?.value || 0,
      ordersTotal: showDelta ? 
        (data.ordersCreated[index]?.delta || 0) + 
        (data.ordersFilled[index]?.delta || 0) + 
        (data.ordersCanceled[index]?.delta || 0) + 
        (data.ordersFailed[index]?.delta || 0)
        : data.ordersTotal[index]?.value || 0,
    }));
  }, [data, showDelta]);

  // Handle metric toggle
  const handleMetricToggle = useCallback((metricKey: string) => {
    setSelectedMetrics(prev => {
      const newSet = new Set(prev);
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey);
      } else {
        newSet.add(metricKey);
      }
      return newSet;
    });
  }, []);

  // Handle line click selection
  const handleLineClick = useCallback((metricKey: string) => {
    setSelectedLine(prev => prev === metricKey ? null : metricKey);
  }, []);

  // Custom legend component
  const CustomLegend = ({ payload }: any) => (
    <div className="custom-legend">
      {METRIC_CONFIGS.map(config => {
        const isSelected = selectedMetrics.has(config.key);
        const isHighlighted = selectedLine === config.key;
        const isHovered = hoveredMetric === config.key;
        
        return (
          <div
            key={config.key}
            className={`legend-item ${isSelected ? 'selected' : 'disabled'} ${isHighlighted ? 'highlighted' : ''}`}
            onClick={() => handleMetricToggle(config.key)}
            onMouseEnter={() => setHoveredMetric(config.key)}
            onMouseLeave={() => setHoveredMetric(null)}
          >
            <div 
              className="legend-color" 
              style={{ 
                backgroundColor: isSelected ? config.color : '#666',
                borderColor: config.color,
                borderStyle: config.strokeDashArray ? 'dashed' : 'solid'
              }} 
            />
            <span className="legend-label">{config.label}</span>
            <span className="legend-value">
              {showDelta ? '∆' : ''}{summary[`total${config.key.replace('orders', '').replace(/^./, str => str.toUpperCase())}` as keyof TradingMetricsSummary]?.toLocaleString() || 0}
            </span>
          </div>
        );
      })}
    </div>
  );

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const timestamp = new Date(label).toLocaleString();
    
    return (
      <div className="custom-tooltip">
        <div className="tooltip-header">{timestamp}</div>
        <div className="tooltip-content">
          {payload
            .filter((entry: any) => selectedMetrics.has(entry.dataKey))
            .map((entry: any) => {
              const config = METRIC_CONFIGS.find(c => c.key === entry.dataKey);
              if (!config) return null;
              
              return (
                <div key={entry.dataKey} className="tooltip-item">
                  <div 
                    className="tooltip-color" 
                    style={{ backgroundColor: config.color }} 
                  />
                  <span className="tooltip-label">{config.label}:</span>
                  <span className="tooltip-value">
                    {showDelta ? '∆' : ''}{entry.value?.toLocaleString() || 0}
                  </span>
                </div>
              );
            })}
        </div>
      </div>
    );
  };

  // Handle brush change for timeline navigation
  const handleBrushChange = useCallback((brushData: any) => {
    if (brushData) {
      setBrushRange({
        startIndex: brushData.startIndex,
        endIndex: brushData.endIndex
      });
      onTimeRangeChange?.(brushData.startIndex, brushData.endIndex);
    }
  }, [onTimeRangeChange]);

  // Handle wheel event for timeline scrolling
  const handleWheel = useCallback((e: WheelEvent) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 1.1 : 0.9;
      // Implement zoom logic here
    }
  }, []);

  useEffect(() => {
    const chartElement = chartRef.current;
    if (chartElement) {
      chartElement.addEventListener('wheel', handleWheel, { passive: false });
      return () => chartElement.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  if (isLoading) {
    return (
      <div className="overlaid-chart-container loading">
        <div className="loading-skeleton">
          <div className="skeleton-summary"></div>
          <div className="skeleton-chart"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="overlaid-chart-container" ref={chartRef}>
      {/* Summary Section */}
      <div className="metrics-summary">
        <div className="summary-grid">
          <div className="summary-item primary">
            <div className="summary-value">{summary.totalCreated?.toLocaleString() || 0}</div>
            <div className="summary-label">Total Created</div>
          </div>
          <div className="summary-item success">
            <div className="summary-value">{summary.totalFilled?.toLocaleString() || 0}</div>
            <div className="summary-label">
              Filled ({summary.filledPercentage?.toFixed(1) || 0}%)
            </div>
          </div>
          <div className="summary-item warning">
            <div className="summary-value">{summary.totalCanceled?.toLocaleString() || 0}</div>
            <div className="summary-label">
              Canceled ({summary.canceledPercentage?.toFixed(1) || 0}%)
            </div>
          </div>
          <div className="summary-item danger">
            <div className="summary-value">{summary.totalFailed?.toLocaleString() || 0}</div>
            <div className="summary-label">
              Failed ({summary.failedPercentage?.toFixed(1) || 0}%)
            </div>
          </div>
          <div className="summary-item info">
            <div className="summary-value">{summary.successRate?.toFixed(1) || 0}%</div>
            <div className="summary-label">Success Rate</div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="chart-section">
        <div className="chart-header">
          <h3>Trading Metrics Overlay</h3>
          <div className="chart-controls">
            <div className="view-mode">
              {showDelta ? 'Delta View' : 'Absolute View'}
            </div>
            <div className="data-info">
              {chartData.length} data points
            </div>
          </div>
        </div>

        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#404040" opacity={0.3} />
            <XAxis 
              dataKey="time"
              tickFormatter={(value) => new Date(value).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
              stroke="#b0b0b0"
              fontSize={12}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis 
              stroke="#b0b0b0"
              fontSize={12}
              tickFormatter={(value) => value.toLocaleString()}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Render lines for each metric */}
            {METRIC_CONFIGS.map(config => {
              const isSelected = selectedMetrics.has(config.key);
              const isHighlighted = selectedLine === config.key;
              const isHovered = hoveredMetric === config.key;
              
              if (!isSelected) return null;
              
              return (
                <Line
                  key={config.key}
                  type="monotone"
                  dataKey={config.key}
                  stroke={config.color}
                  strokeWidth={isHighlighted || isHovered ? config.strokeWidth + 1 : config.strokeWidth}
                  strokeDasharray={config.strokeDashArray}
                  dot={false}
                  activeDot={{ 
                    r: 6, 
                    fill: config.color,
                    stroke: '#fff',
                    strokeWidth: 2
                  }}
                  onClick={() => handleLineClick(config.key)}
                  style={{ 
                    cursor: 'pointer',
                    opacity: selectedLine && selectedLine !== config.key ? 0.3 : 1,
                    filter: isHighlighted ? 'drop-shadow(0 0 4px rgba(255,255,255,0.5))' : 'none'
                  }}
                />
              );
            })}
            
            {/* Timeline Navigation Brush */}
            <Brush
              dataKey="time"
              height={40}
              stroke="#1976d2"
              fill="rgba(25, 118, 210, 0.1)"
              onChange={handleBrushChange}
              tickFormatter={(value) => new Date(value).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Custom Legend */}
        <CustomLegend />
      </div>

      {/* Interactive Instructions */}
      <div className="chart-instructions">
        <div className="instruction-item">
          <span className="instruction-icon">🖱️</span>
          <span>Click legend items to toggle metrics</span>
        </div>
        <div className="instruction-item">
          <span className="instruction-icon">📊</span>
          <span>Click lines to highlight specific metrics</span>
        </div>
        <div className="instruction-item">
          <span className="instruction-icon">⏱️</span>
          <span>Drag timeline brush to navigate time ranges</span>
        </div>
        <div className="instruction-item">
          <span className="instruction-icon">🔍</span>
          <span>Ctrl+Scroll to zoom timeline (coming soon)</span>
        </div>
      </div>
    </div>
  );
};